import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { RiFilter2Fill } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Select from "react-select";
import styled from "styled-components";
import { media } from "../../helpers/responsive";
import { filterOperations } from "../../state/features/filters";
import { type RootState, dispatchAction } from "../../state/store";
import { removeDuplicateArrayItems } from "../../helpers/utils";
import { clearSecondaryFilterFocus } from "../../state/features/analytics/actions";
import { useFetchAnalyticsFilterData } from "../../helpers/hooks/useFetchAnalyticsFilterData";

/* ─── Filter Item ─── */

interface FilterItemProps {
  filterItem: any;
  handleFilterItemSelection: (item: any) => void;
  resetButtonRef: React.MutableRefObject<any>;
  selectedFilters: any[];
  setIsScrolling: (v: boolean) => void;
  isScrolling: boolean;
}

const FilterItem = ({
  filterItem,
  handleFilterItemSelection,
  resetButtonRef,
  selectedFilters,
  setIsScrolling,
  isScrolling,
}: FilterItemProps) => {
  const [filterItemsList, setFilterItemsList] = useState<any[]>([]);
  const [initialRender, setInitialRender] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { mode: theme } = useSelector((state: RootState) => state.theme);
  const { widgets: widgetFilters } = useSelector(
    (state: RootState) => state.filters
  );

  const multiSelectRef = useRef<any>(null);
  const filterLabelRef = useRef<HTMLSpanElement>(null);

  const filterToFocus = useSelector(
    (state: RootState) => state.analytics.focusFilter.widget
  );

  useEffect(() => {
    if (!isMenuOpen && filterToFocus === filterItem.filterKey) {
      setTimeout(() => {
        filterLabelRef?.current?.scrollIntoView({ behavior: "smooth" });
        setTimeout(() => multiSelectRef?.current?.focus(), 500);
      }, 500);
    }
    if (isMenuOpen && filterToFocus === filterItem.filterKey) {
      dispatchAction(clearSecondaryFilterFocus());
    }
  }, [filterToFocus, isMenuOpen]);

  useEffect(() => {
    multiSelectRef.current?.clearValue();
  }, [widgetFilters.length]);

  const highlightedValues = useMemo(() => {
    const match = selectedFilters.find((d) => d.key === filterItem.filterKey);
    if (!match) return [];
    return match.data.map((d: any) => ({
      ...d,
      label: d.name,
      value: d.id,
      data_value: d.value,
    }));
  }, [selectedFilters, filterItem.filterKey]);

  useEffect(() => {
    if (filterItem) {
      const options = filterItem.options.map((f: any) => ({
        ...f,
        label: f.name,
        value: f.id,
        data_value: f.value,
      }));
      setFilterItemsList(removeDuplicateArrayItems(options));
    }
    const resetBtn = resetButtonRef.current;
    const handleReset = () => multiSelectRef.current?.clearValue();
    resetBtn?.addEventListener("click", handleReset);
    return () => resetBtn?.removeEventListener("click", handleReset);
  }, [filterItem]);

  const handleItemSelect = (optionData: any) => {
    setIsMenuOpen(false);
    multiSelectRef?.current?.blur();
    if (initialRender) {
      setInitialRender(false);
      return;
    }
    handleFilterItemSelection({
      key: filterItem.filterKey,
      name: filterItem.name,
      optionData,
    });
  };

  useEffect(() => {
    if (isScrolling) {
      setIsMenuOpen(false);
      setIsScrolling(false);
      multiSelectRef?.current?.blur();
    }
  }, [isScrolling]);

  const selectStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    menu: (base: any) => ({
      ...base,
      fontSize: "0.9rem",
      boxShadow: "rgba(0, 0, 0, 0.2) 5px 5px 10px",
      border: "1px solid rgb(222, 222, 222)",
      borderRadius: "10px",
      maxHeight: "200px",
      width: "250px",
      overflowY: "scroll" as const,
      position: "absolute" as const,
      top: "-40px",
      left: "30px",
      background: theme === "dark" ? "rgb(33, 33, 33)" : "#fff",
      color: theme === "dark" ? "#fff" : "#000",
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: "200px",
      width: "250px",
      borderRadius: "10px",
      background: theme === "dark" ? "rgb(33, 33, 33)" : "#fff",
    }),
    option: (base: any) => ({
      ...base,
      cursor: "pointer",
      background: theme === "dark" ? "rgb(33, 33, 33)" : "#fff",
      "&:hover": { color: "#fff", background: "#3aa98d" },
    }),
  };

  return (
    <>
      <span className="filter-label" ref={filterLabelRef}>
        {filterItem.name}
      </span>
      <Select
        classNamePrefix="custom-filter"
        className="custom-select"
        options={filterItemsList}
        isMulti
        onChange={handleItemSelect}
        value={highlightedValues}
        ref={multiSelectRef}
        menuIsOpen={isMenuOpen}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: () => null,
          ClearIndicator: () => null,
        }}
        menuPortalTarget={document.body}
        styles={selectStyles}
        placeholder="Search here..."
        onFocus={() => {
          setIsMenuOpen(true);
          setIsScrolling(false);
        }}
        onBlur={() => {
          setIsMenuOpen(false);
          setIsScrolling(false);
        }}
      />
    </>
  );
};

/* ─── Lazy Filter Item (SubFilterConfiguration pages) ─── */

interface LazyFilterItemProps {
  meta: { url: string; accessor: string; label: string };
  handleFilterItemSelection: (item: any) => void;
  resetButtonRef: React.MutableRefObject<any>;
  selectedFilters: any[];
  setIsScrolling: (v: boolean) => void;
  isScrolling: boolean;
}

const LazyFilterItem = ({
  meta,
  handleFilterItemSelection,
  resetButtonRef,
  selectedFilters,
  setIsScrolling,
  isScrolling,
}: LazyFilterItemProps) => {
  const [hasOpened, setHasOpened] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [initialRender, setInitialRender] = useState(true);

  const { mode: theme } = useSelector((state: RootState) => state.theme);
  const { widgets: widgetFilters } = useSelector(
    (state: RootState) => state.filters
  );
  const filters = useSelector((state: RootState) => state.filters);
  const filterToFocus = useSelector(
    (state: RootState) => state.analytics.focusFilter.widget
  );

  const routeMatch = useParams();
  const multiSelectRef = useRef<any>(null);
  const filterLabelRef = useRef<HTMLSpanElement>(null);

  // Lazy fetch: SWR is only fired after `hasOpened` flips to true. Subsequent
  // opens within the SWR dedupe window are served from cache; time-range
  // changes invalidate the cache key (filters are part of the URL).
  const { data, isLoading } = useFetchAnalyticsFilterData({
    dashboard: routeMatch.dashboard || "",
    widget: routeMatch.widget || "",
    filterUrl: meta.url,
    filters: { ...filters, widgets: [] },
    shouldFetchFilterData: hasOpened,
  });

  const filterItemsList = useMemo(() => {
    const values = data?.filter_data?.values ?? [];
    const options = values.map((v: any) => ({
      id: v.value,
      name: v.heading ?? v.label,
      value: v.value,
      label: v.heading ?? v.label,
      data_value: v.value,
    }));
    return removeDuplicateArrayItems(options);
  }, [data]);

  const highlightedValues = useMemo(() => {
    const match = selectedFilters.find((d) => d.key === meta.url);
    if (!match) return [];
    return match.data.map((d: any) => ({
      ...d,
      label: d.name,
      value: d.id,
      data_value: d.value,
    }));
  }, [selectedFilters, meta.url]);

  useEffect(() => {
    if (!isMenuOpen && filterToFocus === meta.url) {
      setTimeout(() => {
        filterLabelRef?.current?.scrollIntoView({ behavior: "smooth" });
        setTimeout(() => multiSelectRef?.current?.focus(), 500);
      }, 500);
    }
    if (isMenuOpen && filterToFocus === meta.url) {
      dispatchAction(clearSecondaryFilterFocus());
    }
  }, [filterToFocus, isMenuOpen]);

  useEffect(() => {
    multiSelectRef.current?.clearValue();
  }, [widgetFilters.length]);

  useEffect(() => {
    const resetBtn = resetButtonRef.current;
    const handleReset = () => multiSelectRef.current?.clearValue();
    resetBtn?.addEventListener("click", handleReset);
    return () => resetBtn?.removeEventListener("click", handleReset);
  }, []);

  useEffect(() => {
    if (isScrolling) {
      setIsMenuOpen(false);
      setIsScrolling(false);
      multiSelectRef?.current?.blur();
    }
  }, [isScrolling]);

  const handleItemSelect = (optionData: any) => {
    setIsMenuOpen(false);
    multiSelectRef?.current?.blur();
    if (initialRender) {
      setInitialRender(false);
      return;
    }
    handleFilterItemSelection({
      key: meta.url,
      name: meta.label,
      optionData,
    });
  };

  const selectStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    menu: (base: any) => ({
      ...base,
      fontSize: "0.9rem",
      boxShadow: "rgba(0, 0, 0, 0.2) 5px 5px 10px",
      border: "1px solid rgb(222, 222, 222)",
      borderRadius: "10px",
      maxHeight: "200px",
      width: "250px",
      overflowY: "scroll" as const,
      position: "absolute" as const,
      top: "-40px",
      left: "30px",
      background: theme === "dark" ? "rgb(33, 33, 33)" : "#fff",
      color: theme === "dark" ? "#fff" : "#000",
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: "200px",
      width: "250px",
      borderRadius: "10px",
      background: theme === "dark" ? "rgb(33, 33, 33)" : "#fff",
    }),
    option: (base: any) => ({
      ...base,
      cursor: "pointer",
      background: theme === "dark" ? "rgb(33, 33, 33)" : "#fff",
      "&:hover": { color: "#fff", background: "#3aa98d" },
    }),
  };

  return (
    <>
      <span className="filter-label" ref={filterLabelRef}>
        {meta.label}
      </span>
      <Select
        classNamePrefix="custom-filter"
        className="custom-select"
        options={filterItemsList}
        isMulti
        isLoading={hasOpened && isLoading}
        onChange={handleItemSelect}
        value={highlightedValues}
        ref={multiSelectRef}
        menuIsOpen={isMenuOpen}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: () => null,
          ClearIndicator: () => null,
        }}
        menuPortalTarget={document.body}
        styles={selectStyles}
        placeholder={isLoading && hasOpened ? "Loading…" : "Search here..."}
        noOptionsMessage={() =>
          hasOpened && isLoading ? "Loading…" : "No options"
        }
        onFocus={() => {
          setHasOpened(true);
          setIsMenuOpen(true);
          setIsScrolling(false);
        }}
        onBlur={() => {
          setIsMenuOpen(false);
          setIsScrolling(false);
        }}
      />
    </>
  );
};

/* ─── Secondary Filter Dialog ─── */

const SecondaryFilterDialog = () => {
  const { filterList, analyticsFilters, disableWidgetSourcedFilters } =
    useSelector((state: RootState) => state.analytics);
  const { widgets } = useSelector((state: RootState) => state.filters);
  const focusFilter = useSelector((state: RootState) => state.analytics.focusFilter.widget);

  const [selectedFilters, setSelectedFilters] = useState<
    { name: string; key: string; data: { id: number; name: string; value: number | string }[] }[]
  >([]);
  const [showDialog, setShowDialog] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const dispatch = useDispatch();
  const resetButtonRef = useRef<HTMLButtonElement>(null);

  const handleFilterItemSelection = (item: any) => {
    setSelectedFilters((prev) => {
      let updated = prev.length ? [...prev] : [];
      const index = updated.findIndex((w) => w.key === item.key);
      if (index === -1) {
        updated.push({ name: item.name, key: item.key, data: item.optionData });
      } else {
        updated[index] = { ...updated[index], data: item.optionData };
      }
      return updated.filter((i) => i.data.length);
    });
  };

  useEffect(() => {
    setSelectedFilters(widgets);
  }, [widgets]);

  const openDialog = () => {
    setShowDialog(true);
    setSelectedFilters(widgets);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setSelectedFilters(widgets);
  };

  const resetFilters = () => {
    setSelectedFilters([]);
    dispatch(filterOperations.clearWidgetFilter());
    setShowDialog(false);
  };

  const applyFilters = () => {
    if (selectedFilters.length) {
      const filters = selectedFilters.filter((i) => i.data.length);
      dispatch(filterOperations.setDropdownFilters(filters));
      setShowDialog(false);
    }
  };

  const isApplyDisabled = useMemo(() => {
    if (!selectedFilters.length) return true;
    if (selectedFilters.length !== widgets.length) return false;
    for (const filter of selectedFilters) {
      const current = widgets.find((j) => j.key === filter.key);
      if (filter.data.length !== current?.data?.length) return false;
      const currentIds = current?.data?.map((j: any) => j.id);
      if (filter.data.some((j: any) => !currentIds?.includes(j.id))) return false;
    }
    return true;
  }, [selectedFilters, widgets]);

  const isResetDisabled = !widgets.length;

  useEffect(() => {
    if (focusFilter) openDialog();
  }, [focusFilter]);

  const appliedCount = widgets.length;

  return (
    <Wrapper className="secondary-filter-control">
      <button
        className="filter-trigger"
        onClick={openDialog}
        aria-label="Open filters"
        type="button"
      >
        <RiFilter2Fill color="#2B7BC5" />
        {appliedCount > 0 && (
          <span className="filter-badge">{appliedCount}</span>
        )}
      </button>

      <StyledModal
        show={showDialog}
        onHide={closeDialog}
        centered
        size="lg"
        aria-labelledby="filter-dialog-title"
      >
        <Modal.Header className="dialog-header">
          <Modal.Title id="filter-dialog-title" className="dialog-title">
            Filter your data
          </Modal.Title>
          <button
            className="close-btn"
            onClick={closeDialog}
            aria-label="Close"
            type="button"
          >
            <IoClose />
          </button>
        </Modal.Header>

        <Modal.Body className="dialog-body">
          <div
            className="filter-grid"
            onScroll={() => setIsScrolling(true)}
          >
            {disableWidgetSourcedFilters
              ? analyticsFilters.map((meta, index) => (
                  <div className="filter-item" key={meta.url ?? index}>
                    <LazyFilterItem
                      meta={meta}
                      handleFilterItemSelection={handleFilterItemSelection}
                      resetButtonRef={resetButtonRef}
                      selectedFilters={selectedFilters}
                      setIsScrolling={setIsScrolling}
                      isScrolling={isScrolling}
                    />
                  </div>
                ))
              : filterList.map((filterItem: any, index: number) => (
                  <div
                    className="filter-item"
                    key={filterItem.filterKey ?? index}>
                    <FilterItem
                      filterItem={filterItem}
                      handleFilterItemSelection={handleFilterItemSelection}
                      resetButtonRef={resetButtonRef}
                      selectedFilters={selectedFilters}
                      setIsScrolling={setIsScrolling}
                      isScrolling={isScrolling}
                    />
                  </div>
                ))}
          </div>
        </Modal.Body>

        <Modal.Footer className="dialog-footer">
          <button
            ref={resetButtonRef}
            className="btn btn-reset"
            onClick={resetFilters}
            disabled={isResetDisabled}
            type="button"
          >
            Reset
          </button>
          <button
            className="btn btn-apply"
            onClick={applyFilters}
            disabled={isApplyDisabled}
            type="button"
          >
            Apply
          </button>
        </Modal.Footer>
      </StyledModal>
    </Wrapper>
  );
};

export default SecondaryFilterDialog;

/* ─── Styles ─── */

const Wrapper = styled.div`
  .filter-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.widget.background};
    border: 1px solid transparent;
    border-radius: 8px;
    padding: 9px;
    cursor: pointer;
    transition: border-color 0.25s linear;
    position: relative;

    > svg { font-size: 20px; }

    .filter-badge {
      position: absolute;
      top: -6px;
      right: -6px;
      font-size: 10px;
      font-weight: 600;
      background: #e53935;
      color: #fff;
      border-radius: 50%;
      width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }
  }

  ${media.down.lg} {
    .filter-trigger {
      padding: 6px;
      > svg { font-size: 22px; }
    }
  }
`;

const StyledModal = styled(Modal)`
  .modal-dialog {
    margin: 1rem auto;
    max-width: 700px;

    ${media.down.xs} {
      margin: 0;
      max-width: 100%;
      min-height: 100vh;
      .modal-content {
        min-height: 100vh;
        border-radius: 0;
      }
    }
  }

  .modal-content {
    background: ${({ theme }) => theme.widget.background};
    border: 1px solid ${({ theme }) => theme.border_color};
    border-radius: 16px;
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.12);
    overflow: hidden;
  }

  .dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 12px;
    border-bottom: none;

    .dialog-title {
      font-size: 22px;
      font-weight: 700;
      line-height: 1.3;
      margin: 0;
    }

    .close-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: 1px solid ${({ theme }) => theme.border_color};
      border-radius: 50%;
      width: 36px;
      height: 36px;
      cursor: pointer;
      color: ${({ theme }) => theme.text};
      font-size: 20px;
      flex-shrink: 0;
      transition: background 0.15s;
      &:hover { background: ${({ theme }) => theme.body}; }
    }
  }

  .dialog-body {
    padding: 0 24px;

    .filter-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-auto-rows: 80px;
      gap: 16px;
      max-height: 340px;
      overflow-y: auto;
      padding: 10px 0 4px;

      ${media.down.xs} {
        grid-template-columns: 1fr;
        grid-auto-rows: 76px;
        max-height: calc(100vh - 200px);
      }
    }

    .filter-item {
      font-size: 14px;
      border: 1px solid ${({ theme }) => theme.border_color};
      border-radius: 8px;
      position: relative;
      padding: 0.5rem 0;

      > .filter-label {
        font-weight: 600;
        font-size: 12px;
        display: block;
        position: absolute;
        padding: 0 6px;
        top: -0.6rem;
        left: 0.5rem;
        z-index: 5;
        background: ${({ theme }) => theme.dropdown.background_color};
        line-height: 1;
      }

      .custom-select .custom-filter__control {
        height: 58px;
        box-shadow: none;
        border: none;
        background: ${({ theme }) => theme.dropdown.background_color};
        color: ${({ theme }) => theme.text};

        &.custom-filter__control--is-focused {
          border-color: #0d8a6a;
        }

        .custom-filter__value-container {
          cursor: text;
          font-weight: 400;
          height: 100%;
          overflow-y: auto;
          align-items: flex-start;

          .custom-filter__multi-value {
            background: #198754;
            color: #fff;
            .custom-filter__multi-value__label { color: #fff; }
            .custom-filter__multi-value__remove { background: #146c43; }
          }
        }

        .custom-filter__indicators { cursor: pointer; }
      }
    }
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 24px 20px;
    border-top: none;

    .btn {
      padding: 8px 0;
      min-width: 120px;
      height: 40px;
      font-weight: 500;
      font-size: 14px;
      border-radius: 8px;
    }

    .btn-reset {
      background: transparent;
      border: 1px solid ${({ theme }) => theme.border_color};
      color: ${({ theme }) => theme.text};
    }

    .btn-apply {
      background: ${({ theme }) => theme.button.primary_color};
      border: none;
      color: #fff;
      &:disabled { opacity: 0.5; }
    }

    ${media.down.xs} {
      .btn { flex: 1; min-width: 0; }
    }
  }
`;
